import { useEffect, useState } from "react";
import { useCheckoutContext } from "context/CheckoutContext";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { validatePromoCode } from "api";
import { BsChevronDown, BsChevronUp, BsCart } from "react-icons/bs";

const OrderStatus = ({ cart, isMobile = false }) => {
  const [toggle, setToggle] = useState(true);
  const { checkout, setCheckout } = useCheckoutContext();
  const { currentStep } = checkout;
  const [currentOrderTotal, setCurrentOrderTotal] = useState(0);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeMessage, setPromoCodeMessage] = useState("");
  const [promoCodeStatus, setPromoCodeStatus] = useState(""); // "success", "error", or ""
  const [appliedPromoCode, setAppliedPromoCode] = useState(null); // { code: "", discount: 0, description: "" }
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  const shippingValue = () => {
    return currentStep === 0
      ? "Calculated at next step"
      : checkout.cart.shippingTotalString;
  };

  const discountPointsValue = () => {
    return checkout.applyPoints
      ? `-$ ${currentPoinTotal().toFixed(2)}`
      : "$0.00";
  };

  const discountValue = () => {
    return currentStep === 0
      ? "Calculated at next step"
      : checkout.cart.totalDiscountRcString;
  };

  const discountOrderValue = () => {
    return currentStep === 0
      ? "Calculated at next step"
      : checkout.cart.discountOrder > 0
        ? `-$ ${checkout.cart.discountOrder.toFixed(2)}`
        : "$0.00";
  };

  const taxValue = () => {
    return currentStep === 0
      ? "Calculated at next step"
      : checkout.cart.taxTotalString;
  };

  const currentPoinTotal = () => {
    const totalAmount = parseFloat(cart.subTotalString.replace("$", ""));
    const discountPoints =
      cart.pointsTotal > totalAmount ? totalAmount : cart.pointsTotal;
    return checkout.applyPoints ? discountPoints : 0;
  };

  const handlePromoCodeValidation = async () => {
    if (!promoCode.trim()) {
      setPromoCodeStatus("error");
      setPromoCodeMessage("Please enter a promo code");
      return;
    }
    
    setIsValidatingPromo(true);
    try {
      const response = await validatePromoCode(promoCode, checkout.shopUrl, checkout.shipping, checkout.cartToken, checkout.shopifyCustomer.customerTypeID);
      if (response.isValid) {
        setPromoCodeStatus("success");
        setPromoCodeMessage(response.message);
        setAppliedPromoCode({
           code: response.code,
           discount: response.discount,
           description: response.description,
           isValid: true
         });
        setPromoCode("");
        setCheckout({
          ...checkout,
          promoCode: response.code
        });
      } else {
        setPromoCodeStatus("error");
        setPromoCodeMessage(response.message);
       }
    } catch (error) {
      setPromoCodeStatus("error");
      setPromoCodeMessage("Error validating promo code. Please try again.");
    } finally {
      setIsValidatingPromo(false);
    }
  }

  useEffect(() => {
    const newTotal =
      parseFloat(checkout.cart.subTotalString.replace(/[^0-9.-]+/g, "")) -
      currentPoinTotal() +
      checkout.cart.shippingTotal +
      checkout.cart.taxTotal +
      (checkout.cart.totalDiscountRC || 0) -
      (checkout.cart.discountOrder || 0) -
      (appliedPromoCode?.discount || 0);
    setCurrentOrderTotal(newTotal > 0 ? newTotal : 0);
  }, [checkout.cart, checkout.applyPoints, appliedPromoCode]);

  return (
    <Col 
      xs={12} 
      md={6} 
      className={isMobile 
        ? "fixPadding d-md-none .d-lg-block .d-lg-none .d-xl-block .d-md-none .d-lg-block" 
        : "d-none d-sm-block"
      }
    >
      <div className={`mainContainer ${isMobile ? "newClass" : "rightContainer"}`}>
        {isMobile && (
          <div className="w-100">
            {toggle ? (
              <>
                <div className="d-flex justify-content-between align-items-center w-100 p-3">
                  <div>
                    <BsCart />
                  </div>
                  <div>
                    <button
                      className="transparent"
                      onClick={() => setToggle(false)}
                    >
                      Hide Cart Content <BsChevronUp />
                    </button>
                  </div>
                  <div>
                    <b>
                      {currentOrderTotal.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      })}
                    </b>
                  </div>
                </div>
              </>
            ) : (
              <div className="d-flex justify-content-between align-items-center w-100 p-3">
                <div>
                  <BsCart />
                </div>
                <div>
                  <button
                    className="transparent"
                    onClick={() => setToggle(true)}
                  >
                    Show Cart Content <BsChevronDown />
                  </button>
                </div>
                <div>
                  <b>
                    {currentOrderTotal.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </b>
                </div>
              </div>
            )}
          </div>
        )}
        
        {(!isMobile || toggle) && (
          <Col xs={12} md={6}>
            <div className={isMobile ? "wrapperXs" : "wrapper col-md-12"}>
              <h4 className={`grayFont ${isMobile ? "mt-2" : ""}`}>Today's Order</h4>
              <div className="horizontalRuler" />

              <table className={`productTable ${isMobile ? "smallTable" : ""}`}>
                <tbody>
                  {cart?.orderItems?.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <div className="productImgContainer">
                          <img
                            className="productImg"
                            src={item.imageUrl}
                            alt="product-img"
                          />
                          <span className="productQuantity">
                            {item.quantity.toFixed(0)}
                          </span>
                        </div>
                      </td>
                      <td className={`grayFont ${!isMobile ? "truncate-md" : ""} product-title`}>
                        <b>{item.productTitle}</b>
                      </td>
                      <td className="grayFont">
                        <b>${(item.price * item.quantity).toFixed(2)}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {cart?.orderItems?.length > 0 && (
                <Row className="align-items-center mb-3">
                  <Col xs={12}>
                    <div className="horizontalRuler my-3" />
                  </Col>
                  <Col xs={6} md={isMobile ? 6 : 9}>
                    OOLA MOOLA: <b>${cart.pointsTotal.toFixed(2)}</b>
                  </Col>
                  {cart.pointsTotal > 0 && (
                    <Col xs={6} md={isMobile ? 6 : 3}>
                      <Button
                        className={`btn-bold btn-sm px-4 ${
                          checkout.applyPoints ? "btn-secondary" : "oola-main-color"
                        } float-end`}
                        variant="primary"
                        disabled={cart.pointsTotal <= -2}
                        onClick={() =>
                          setCheckout(prev => ({
                            ...prev,
                            applyPoints: !prev.applyPoints,
                          }))
                        }
                      >
                        {checkout.applyPoints ? "APPLIED" : "APPLY"}
                      </Button>
                    </Col>
                  )}
                  {!isMobile && (
                    <Col xs={12}>
                      <div className="horizontalRuler my-3" />
                    </Col>
                  )}
                </Row>
              )}

              {cart && cart.autoOrderItems && cart.autoOrderItems.length > 0 && (
                <>
                  <h4 className={`grayFont ${isMobile ? "mt-2" : ""}`}>Subscription Order</h4>
                  <div className="horizontalRuler" />
                  <table className={`productTable ${isMobile ? "smallTable" : ""}`}>
                    <tbody>
                      {cart.autoOrderItems.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <div className="productImgContainer">
                              <img
                                className="productImg"
                                src={item.imageUrl}
                                alt="product-img"
                              />
                              <span className="productQuantity">
                                {item.quantity.toFixed(0)}
                              </span>
                            </div>
                          </td>
                          <td className={`grayFont ${!isMobile ? "truncate-md" : ""} product-title`}>
                            <b>{item.productTitle}</b>
                          </td>
                          <td className="grayFont">
                            <b>${(item.price * item.quantity).toFixed(2)}</b>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {cart?.orderItems?.length > 0 && checkout.orderStatus !== "valid" && currentStep > 0 && (
              <Row className={`align-items-center mb-3 ${!isMobile ? "promocode-section" : ""}`}>
                <Col xs={12}>
                  <div className="horizontalRuler my-3" />
                </Col>
                <Col xs={12}>
                  <div className="d-flex align-items-center">
                    {isMobile ? (
                      <div className="form-floating flex-grow-1">
                        <Form.Control
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          className={`form-control promo-input-mobile ${
                            promoCodeStatus === "error" ? "is-invalid" : 
                            promoCodeStatus === "success" ? "is-valid" : ""
                          }`}
                        />
                        <div className="invalid-feedback"></div>
                      </div>
                    ) : (
                      <div className="flex-grow-1">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          placeholder="Discount code or gift card"
                          className={`promo-input-desktop ${
                            promoCodeStatus === "error" ? "is-invalid" : 
                            promoCodeStatus === "success" ? "is-valid" : ""
                          }`}
                        />
                      </div>
                    )}
                    <Button
                      className={`btn-bold btn-sm px-4 oola-main-color promo-apply-btn ${!isMobile ? 'promo-apply-btn-desktop' : ''}`}
                      variant="primary"
                      onClick={handlePromoCodeValidation}
                    >
                      {isValidatingPromo ? "Validating..." : "Apply"}
                    </Button>
                  </div>
                  {promoCodeMessage && (
                    <div
                      className={`promo-feedback ${
                        promoCodeStatus === "success" ? "valid-feedback" : "invalid-feedback"
                      }`}
                    >
                      {promoCodeMessage}
                      {promoCodeStatus === "success" && (
                        <button
                          type="button"
                          className="btn btn-link p-0 ms-2 promo-remove-btn"
                          onClick={() => {
                            setAppliedPromoCode(null);
                            setPromoCodeMessage("");
                            setPromoCodeStatus("");
                            setCheckout({
                              ...checkout,
                              promoCode: ""
                            });
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )}
                </Col>
              </Row>
              )}

              <div className="horizontalRuler" />
              <table className="productPriceTable">
                <tbody>
                  <tr>
                    <td>Sub Total</td>
                    <td>
                      <b>{cart.subTotalString}</b>
                    </td>
                  </tr>
                  {checkout.applyPoints && (
                    <tr>
                      <td>Discount OOLA MOOLA</td>
                      <td>{discountPointsValue()}</td>
                    </tr>
                  )}
                  {checkout.cart.totalDiscountRC < 0 && (
                    <tr>
                      <td>Discount GET10</td>
                      <td>{discountValue()}</td>
                    </tr>
                  )}
                  {checkout.cart.discountOrder > 0 && (
                    <tr>
                      <td>Multiple Event Discount</td>
                      <td>{discountOrderValue()}</td>
                    </tr>
                  )}
                  {appliedPromoCode && appliedPromoCode.isValid && (
                    <tr>
                      <td>Discount {appliedPromoCode.code}</td>
                      <td>-${appliedPromoCode.discount.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr>
                    <td>Shipping</td>
                    <td>{shippingValue()}</td>
                  </tr>
                  <tr>
                    <td>Tax</td>
                    <td>{taxValue()}</td>
                  </tr>
                </tbody>
              </table>

              <div className="horizontalRuler" />
              <div className="priceGridContainer">
                <div className="totalLabelRow">Total</div>
                <div className="totalRow">
                  {currentOrderTotal.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
            </div>
          </Col>
        )}
      </div>
    </Col>
  );
};

export default OrderStatus;
